'use client';

import { MessageBroker } from '@/utils/message-broker';
import { MESSAGES, type Message } from '@/utils/message-broker/types';
import { useEffect } from 'react';
import { useAccountUm } from '../use-account-um';
import { realityCheckService } from '@/logic/reality-check/reality-check-service';
import { useStore } from '@/store/store';
import { POPUPS } from '@/app/components/popup/constants';
import { useGlobalContext } from '@/store/contexts/global-context';

export const useMessageListener = () => {
    const { isAndroidWrapper } = useGlobalContext();
    const { logOutWithModal } = useAccountUm();
    const hideModal = useStore((state) => state.realityCheck.hideModal);
    const closePopup = useStore((state) => state.popup.closePopup);
    const removePopupByType = useStore((state) => state.popup.removePopupByType);

    const handleReloadRequired = (msg: Message) => {
        // Read isLoggedIn from the live store, not the SSR-captured GlobalContext:
        // when a logged-out user completes the deposit-CTA flow via the
        // state-selector, useGlobalContext().isLoggedIn would still be the
        // stale SSR `false` and we'd incorrectly fire location.reload() on a
        // user who is actually logged in.
        const isLoggedIn = useStore.getState().userContext.isLoggedIn;

        if (!isLoggedIn && msg.origin.includes('launcher')) {
            return location.reload();
        }

        if (!isLoggedIn && isAndroidWrapper) {
            location.reload();
        }
    };

    const handleLogoutRequired = () => {
        logOutWithModal();
    };

    const handleVerifyRealityCheck = () => {
        realityCheckService.verifyRealityCheckTimer();
    };

    const handleCloseRealityCheckModal = () => {
        hideModal();
        closePopup();
        removePopupByType(POPUPS.REALITY_CHECK);
        realityCheckService.continueRealityCheck();
    };

    const handleAccountHubShowBalanceChanged = (msg: Message) => {
        if (!isAndroidWrapper) return;
        const data = msg.data as { type: string; showBalance?: boolean };
        useStore.getState().userContext.accountSettings.setShowBalance(!!data.showBalance);
    };

    const handleMessage = (message: Message) => {
        const MESSAGES_MAPPING = {
            [MESSAGES.LOGOUT_REQUIRED]: handleLogoutRequired,
            [MESSAGES.RELOAD_REQUIRED]: handleReloadRequired,
            [MESSAGES.VERIFY_REALITY_CHECK]: handleVerifyRealityCheck,
            [MESSAGES.CLOSE_REALITY_CHECK_MODAL]: handleCloseRealityCheckModal,
            [MESSAGES.ACCOUNT_HUB_SHOW_BALANCE_CHANGED]: handleAccountHubShowBalanceChanged
        };

        const fn = MESSAGES_MAPPING[message.data.type];
        if (fn) fn(message);
    };

    useEffect(() => {
        const cleanup = MessageBroker.register();

        MessageBroker.subscribe(handleMessage);

        return () => {
            MessageBroker.unsubscribe(handleMessage);
            cleanup();
        };
    }, []);
};
